import type {
  AICaptionMediaItem,
  ApprovalRequest,
  ApprovalRequestStatusUpdate,
  BlueskyScheduledPost,
  BulkScheduledPostRequest,
  BulkScheduledPostResponse,
  Event,
  FacebookScheduledPost,
  GenerateAICaptionsResponse,
  GetLinkedinFollowersSearchParams,
  GetLinkedinMemberDetailsParams,
  InstagramScheduledPost,
  LinkedInScheduledPost,
  ListRSSFeedsResponse,
  MemberProfilesResponse,
  PinterestScheduledPost,
  PostReorderRequest,
  RecommendedPublishTimesResponse,
  ReorderResponse,
  RSSFeedRequest,
  RSSFeedResponse,
  RSSFeedValidationRequest,
  RSSFeedValidationResponse,
  SnapchatScheduledPost,
  ThreadsScheduledPost,
  TikTokScheduledPost,
  TwitterScheduledPost,
  UpdateFacebookRequest,
  UpdateInstagramRequest,
  UpdateLinkedInRequest,
  UpdatePinterestRequest,
  UpdateRSSFeedRequest,
  UpdateSnapchatRequest,
  UpdateTikTokRequest,
  UpdateYouTubeRequest,
  YouTubeScheduledPost,
} from '@dashhudson/api-client/scheduler/model';
import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import type { HttpRequestConfig } from '@/types/http';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.schedulerApiUrl, {
  camelizeKeys: true,
  decamelizeIgnoreKeys: ['pinterestBoard'],
});

// `platform` is the URL segment (`facebook`, `instagram`, `tiktok`, …) chosen
// at call sites; we keep it as `string` to avoid coupling the API surface to
// the app-level Platform enum.
type Platform = string;

// Union of every per-platform scheduled-post shape returned by the per-platform
// scheduled_posts endpoints. The caller picks the runtime platform, so the
// concrete type can only be narrowed downstream. Named `PlatformScheduledPost`
// to avoid clashing with the app-domain `ScheduledPost` in
// `@/app/scheduler/types/post.ts` — that one is a loose hand-rolled shape; this
// is the spec-generated wire union.
export type PlatformScheduledPost =
  | BlueskyScheduledPost
  | FacebookScheduledPost
  | InstagramScheduledPost
  | LinkedInScheduledPost
  | PinterestScheduledPost
  | SnapchatScheduledPost
  | ThreadsScheduledPost
  | TikTokScheduledPost
  | TwitterScheduledPost
  | YouTubeScheduledPost;

// Body accepted by POST /{platform}/scheduled_posts and its /batch sibling —
// the spec models the create payload as the full ScheduledPost shape per
// platform.
export type CreatePostRequest = PlatformScheduledPost;

// Body accepted by PATCH /{platform}/scheduled_posts/{id}. Most platforms have
// a dedicated Update*Request shape; Bluesky/Threads/Twitter share the
// ScheduledPost shape on patch (no Update*Request type generated).
export type UpdatePostRequest =
  | UpdateFacebookRequest
  | UpdateInstagramRequest
  | UpdateLinkedInRequest
  | UpdatePinterestRequest
  | UpdateSnapchatRequest
  | UpdateTikTokRequest
  | UpdateYouTubeRequest
  | BlueskyScheduledPost
  | ThreadsScheduledPost
  | TwitterScheduledPost;

export interface FetchPostsParams {
  platform: Platform;
  brandId?: number;
  brandIds?: number[] | string;
  start?: string;
  end?: string;
  sort?: string;
  status?: string[];
  limit?: number;
  hasScheduledTime?: boolean;
}

// Response is the per-platform List*PostsResponse shape (only a subset of
// platforms have a typed list response in the spec), so we leave the response
// loose and let callers narrow.
export const fetchPosts = (
  {
    platform,
    brandId,
    brandIds,
    start,
    end,
    sort,
    status,
    limit,
    hasScheduledTime,
  }: FetchPostsParams,
  config: HttpRequestConfig = {},
) =>
  http.get(`/${platform}/scheduled_posts`, {
    ...config,
    params: {
      brandId,
      brandIds,
      start,
      end,
      sort,
      status: status?.join?.(','),
      limit,
      hasScheduledTime,
      mediaV2: true,
    },
  });

export const fetchPost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.get<PlatformScheduledPost>(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const createPost = (
  { platform, data }: { platform: Platform; data: CreatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.post(`/${platform}/scheduled_posts`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const batchCreatePost = (
  { platform, data }: { platform: Platform; data: CreatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.post<PlatformScheduledPost[]>(`/${platform}/scheduled_posts/batch`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const fetchPostGroup = (
  { postGroupId, brandId }: { postGroupId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.get<{ data: PlatformScheduledPost[] }>(`/scheduled_posts/groups/${postGroupId}`, {
    ...config,
    params: {
      brandId,
      resolveReferences: true,
      mediaV2: true,
    },
  });

export const createBulkPosts = (
  {
    posts,
    bulkSessionId,
    brandId,
  }: { posts: BulkScheduledPostRequest['posts']; bulkSessionId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.post<BulkScheduledPostResponse>(
    '/scheduled_posts/bulk',
    { posts, bulkSessionId, brandId },
    config,
  );

export const updatePost = (
  { platform, id, data }: { platform: Platform; id: number; data: UpdatePostRequest },
  config: HttpRequestConfig = {},
) =>
  http.patch(`/${platform}/scheduled_posts/${id}`, data, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const deletePost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.delete<void>(`/${platform}/scheduled_posts/${id}`, {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const reorderPost = (
  { platform, id, brandId, after }: { platform: Platform; id: number } & PostReorderRequest,
  config: HttpRequestConfig = {},
) =>
  http.post<ReorderResponse>(
    `/${platform}/scheduled_posts/${id}/reorder`,
    { after, brandId },
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const fetchEvents = ({ brandId }: { brandId: number }, config: HttpRequestConfig = {}) =>
  http.get<Event[]>('/events', {
    ...config,
    params: { brandId },
  });

export const createEvent = (
  { brandId, title, startDate, endDate, description, color }: Event,
  config: HttpRequestConfig = {},
) =>
  http.post<Event>(
    '/events',
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const updateEvent = (
  { id, brandId, title, startDate, endDate, description, color }: Event,
  config: HttpRequestConfig = {},
) =>
  http.patch<Event>(
    `/events/${id}`,
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const deleteEvent = ({ id }: { id: number }, config: HttpRequestConfig = {}) =>
  http.delete<void>(`/events/${id}`, config);

export const updateApprovalRequest = (
  {
    approvalRequestId,
    approvalStatus,
  }: { approvalRequestId: number; approvalStatus: ApprovalRequestStatusUpdate['approvalStatus'] },
  config: HttpRequestConfig = {},
) =>
  http.patch<ApprovalRequest>(
    `/approval_requests/${approvalRequestId}`,
    { approval_status: approvalStatus },
    config,
  );

export async function getRecommendedPublishTimes(
  { brandId, platform, timezone }: { brandId: number; platform: string; timezone: string },
  config: HttpRequestConfig = {},
) {
  return http.get<RecommendedPublishTimesResponse>(`/recommended_publish_times`, {
    ...config,
    params: { brandId, platform_type: platform.toUpperCase(), timezone },
  });
}

/**
 * Validates an RSS feed URL with brand and name context.
 */
export async function validateRssFeed(
  { url, brandId, name, rssFeedId }: RSSFeedValidationRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedValidationRequest = { url, brandId, name };
  if (rssFeedId) {
    data.rssFeedId = rssFeedId;
  }
  return http.post<RSSFeedValidationResponse>(`/rss_feeds/validate`, data, config);
}

/**
 * Retrieves a list of RSS feeds for specified brand IDs. `url`, when provided,
 * is parsed for additional query params (used to follow pagination cursors).
 */
export async function getRssFeedsList(
  {
    brandIds,
    url,
    limit,
    orderByBrandIds,
  }: {
    brandIds: number[] | number | string;
    url?: string;
    limit?: number;
    orderByBrandIds?: boolean;
  },
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get<ListRSSFeedsResponse>(`/rss_feeds`, {
    ...config,
    params: {
      ...params,
      brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
      limit,
      orderByBrandIds,
    },
  });
}

/**
 * Retrieves a single RSS feed by ID.
 */
export async function getRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.get<RSSFeedResponse>(`/rss_feeds/${id}`, config);
}

/**
 * Creates a new RSS feed with channel configurations.
 */
export async function createRssFeed(
  { brandId, url, name, frequency, status, enabled, channelConfigs, sampleItem }: RSSFeedRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedRequest = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.post<RSSFeedResponse>(`/rss_feeds`, data, config);
}

/**
 * Updates an existing RSS feed with new configurations. `brandId` is not in
 * the spec's UpdateRSSFeedRequest but our callers send it; the backend
 * tolerates it so we preserve the existing wire payload.
 */
export async function updateRssFeed(
  {
    id,
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  }: UpdateRSSFeedRequest & { id: number; brandId?: number },
  config: HttpRequestConfig = {},
) {
  const data: UpdateRSSFeedRequest & { brandId?: number } = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.patch<RSSFeedResponse>(`/rss_feeds/${id}`, data, config);
}

/**
 * Deletes an RSS feed by ID.
 */
export async function deleteRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.delete<void>(`/rss_feeds/${id}`, config);
}

/**
 * Search for LinkedIn organization followers for @mentions.
 */
export async function searchLinkedInFollowers(
  { brandId, keyword }: GetLinkedinFollowersSearchParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/linkedin/followers/search`, {
    params: { brand_id: brandId, keyword },
    ...config,
  });
}

/**
 * Get LinkedIn member details (avatar, name, headline) by URNs.
 */
export async function getLinkedInMemberDetails(
  { brandId, personUrns }: GetLinkedinMemberDetailsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<MemberProfilesResponse>(`/linkedin/member_details`, {
    params: { brand_id: brandId, person_urns: personUrns },
    paramsSerializer: {
      indexes: null, // serializes as person_urns=x&person_urns=y (no brackets)
    },
    ...config,
  });
}

/**
 * Generate AI captions for bulk media items. `platform` and `mediaType` are
 * loosened to `string` so callers can forward the runtime platform/media
 * strings they hold — the spec types them as enums but the wire contract
 * accepts the raw string values.
 */
export interface GenerateAICaptionsParams {
  brandId: number;
  platform: string;
  mediaItems: Array<Omit<AICaptionMediaItem, 'mediaType'> & { mediaType: string }>;
}

export async function generateAICaptions(
  { brandId, platform, mediaItems }: GenerateAICaptionsParams,
  config: HttpRequestConfig = {},
) {
  return http.post<GenerateAICaptionsResponse>(
    '/ai_captions',
    { brandId, platform, mediaItems },
    { timeout: 30000, ...config },
  );
}
